import React, { useState, useEffect } from 'react';
import { MessageSquare, Search, Shield, Trash2, Hash, Eye } from 'lucide-react';
import { supabase } from '../supabaseClient'; // Make sure this path is correct!

const STANDARD_CHANNELS = [
  { id: 'general-chat', name: 'General Lounge' },
  { id: 'vip-signals', name: 'VIP Trade Signals' },
  { id: 'crypto-analysis', name: 'Crypto Analysis' },
  { id: 'forex-beginners', name: 'Forex Beginners' },
  { id: 'risk-management-pro', name: 'Risk Management Pro' }
];

const LiveChatMonitor = ({ communities }) => {
    const [selectedHub, setSelectedHub] = useState('global');
    const [selectedChannel, setSelectedChannel] = useState('general-chat');
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // --- FETCH & SUBSCRIBE TO LIVE DATA ---
    useEffect(() => {
        fetchMessages();

        // Set up Supabase Realtime Subscription
        const channelSub = supabase
            .channel('public:community_messages')
            .on('postgres_changes', { 
                event: 'INSERT', 
                schema: 'public', 
                table: 'community_messages' 
            }, (payload) => {
                const newMsg = payload.new;
                // Only add to state if it belongs to the exact Hub and Channel we are watching
                const isCorrectHub = selectedHub === 'global' ? newMsg.channel_id === null : newMsg.channel_id === selectedHub;
                const isCorrectChannel = newMsg.channel === selectedChannel;

                if (isCorrectHub && isCorrectChannel) {
                    setMessages(prev => [...prev, newMsg]);
                }
            })
            .on('postgres_changes', {
                event: 'DELETE',
                schema: 'public',
                table: 'community_messages'
            }, (payload) => {
                // Remove message from UI if someone else (or a mod) deletes it
                setMessages(prev => prev.filter(msg => msg.id !== payload.old.id));
            })
            .subscribe();

        return () => {
            supabase.removeChannel(channelSub);
        };
    }, [selectedHub, selectedChannel]);

    const fetchMessages = async () => {
        setIsLoading(true);
        try {
            let query = supabase
                .from('community_messages')
                .select('*')
                .eq('channel', selectedChannel)
                .order('created_at', { ascending: false })
                .limit(50); // Grab the last 50 messages

            // Handle "Global Nommia" vs "Specific White-Label Hub"
            if (selectedHub === 'global') {
                query = query.is('channel_id', null);
            } else {
                query = query.eq('channel_id', selectedHub);
            }

            const { data, error } = await query;
            
            if (error) throw error;
            
            if (data) {
                // Reverse the array so the newest messages appear at the bottom of the chat UI
                setMessages(data.reverse());
            }
        } catch (error) {
            console.error("Error fetching live messages:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // --- LIVE DELETE HANDLER ---
    const handleDeleteMessage = async (id) => {
        if(window.confirm("WARNING: This will permanently delete this message from the database. Proceed?")) {
            try {
                // 1. Delete from Supabase
                const { error } = await supabase
                    .from('community_messages')
                    .delete()
                    .eq('id', id);

                if (error) throw error;

                // 2. Remove from local UI state (though our real-time listener will also catch this)
                setMessages(messages.filter(m => m.id !== id));
            } catch (error) {
                console.error("Failed to delete message:", error);
                alert("Failed to delete message.");
            }
        }
    };

    // Helper to format timestamps
    const formatTime = (isoString) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl mt-8">
            {/* Monitor Header & Controls */}
            <div className="p-4 border-b border-slate-800 bg-slate-950/50 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h3 className="font-bold text-white flex items-center gap-2">
                        <Eye className="text-blue-500" /> Live Chat Monitor
                    </h3>
                    <p className="text-xs text-slate-400 mt-1">Actively spectate and moderate channels across all communities.</p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                    {/* Hub Selector */}
                    <select 
                        className="bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm text-white focus:outline-none focus:border-blue-500 cursor-pointer"
                        value={selectedHub}
                        onChange={(e) => setSelectedHub(e.target.value)}
                    >
                        <option value="global" className="font-bold text-amber-500">🌐 Global Nommia (Master)</option>
                        <optgroup label="Partner Hubs">
                            {communities && communities.map(hub => (
                                <option key={hub.id} value={hub.id}>🔒 {hub.name}</option>
                            ))}
                        </optgroup>
                    </select>

                    {/* Channel Selector */}
                    <select 
                        className="bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm text-white focus:outline-none focus:border-blue-500 cursor-pointer"
                        value={selectedChannel}
                        onChange={(e) => setSelectedChannel(e.target.value)}
                    >
                        {STANDARD_CHANNELS.map(channel => (
                            <option key={channel.id} value={channel.id}># {channel.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Chat Feed */}
            <div className="h-80 bg-slate-950 p-4 overflow-y-auto space-y-4 custom-scrollbar">
                {isLoading ? (
                    <div className="flex justify-center items-center h-full text-slate-500 text-sm">
                        <span className="animate-pulse">Loading live feed...</span>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex justify-center items-center h-full text-slate-600 text-sm italic">
                        No messages in this channel yet.
                    </div>
                ) : (
                    messages.map((msg) => {
                        const isMod = msg.role === 'Community Lead' || msg.role === 'Admin';
                        return (
                            <div key={msg.id} className="flex gap-3 group p-2 rounded-lg transition-colors hover:bg-slate-900/50">
                                {/* Avatar */}
                                <div className={`w-8 h-8 rounded shrink-0 flex items-center justify-center font-bold text-xs ${isMod ? 'bg-amber-500 text-slate-900' : 'bg-slate-800 text-slate-400'}`}>
                                    {msg.avatar || 'U'}
                                </div>

                                {/* Message Body */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline gap-2 mb-1">
                                        <span className={`text-sm font-bold ${isMod ? 'text-amber-500' : 'text-white'}`}>
                                            {msg.user_name || 'Unknown User'}
                                        </span>
                                        {isMod && <Shield size={12} className="text-amber-500" />}
                                        <span className="text-[10px] text-slate-500">{formatTime(msg.created_at)}</span>
                                        {/* Optional: Add flag logic here later if your schema supports it */}
                                    </div>
                                    <p className="text-sm text-slate-300 break-words">{msg.text_content}</p>
                                </div>

                                {/* Mod Actions (Visible on hover) */}
                                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-start">
                                    <button 
                                        onClick={() => handleDeleteMessage(msg.id)}
                                        className="p-1.5 text-slate-500 hover:text-red-500 hover:bg-red-500/10 rounded transition-colors"
                                        title="Delete Message"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
            
            {/* Live Indicator */}
            <div className="bg-slate-900 border-t border-slate-800 p-2 flex justify-center items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Connected to Live Supabase Feed</span>
            </div>
        </div>
    );
};

export default LiveChatMonitor;